import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getReportsOverview, getReportsSites, getReportsSiteDetail,
  getReportsChargers, getReportsChargerDetail, getReportsSessions,
  exportReportsOverviewPDF,
  exportReportsSessionsCSV, exportReportsSessionsPDF,
  exportReportsSitesCSV, exportReportsSitesPDF,
  exportReportsChargersCSV, exportReportsChargersPDF,
  exportReportsChargerDetailPDF, exportReportsSiteDetailPDF,
  getSites
} from '../lib/api';
import { useWebSocket, EventTypes } from '../context/WebSocketContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BarChart3, Calendar, Zap, Wallet, Activity, Download, FileSpreadsheet, 
  FileText, TrendingUp, TrendingDown, Building2, BatteryCharging, Clock,
  ChevronRight, ArrowLeft, Eye, MapPin, Users, Filter, RefreshCw, Radio
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency as formatMoney, formatEnergy, formatAlbanianDate } from '../lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#1d62a7', '#afd039', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Sot' },
  { value: '7d', label: '7 Ditë' },
  { value: '30d', label: '30 Ditë' },
  { value: '90d', label: '3 Muaj' },
  { value: '365d', label: '1 Vit' },
  { value: 'custom', label: 'Personalizuar' },
];

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "primary" }) => {
  const colorClasses = {
    primary: "bg-primary/10 text-primary",
    blue: "bg-blue-500/10 text-blue-500",
    green: "bg-green-500/10 text-green-500",
    yellow: "bg-yellow-500/10 text-yellow-500",
    purple: "bg-purple-500/10 text-purple-500"
  };
  
  return (
    <Card className="bg-card border-border hover:shadow-md transition-shadow">
      <CardContent className="p-4 md:p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs md:text-sm text-muted-foreground">{title}</p>
              <p className="text-xl md:text-2xl font-semibold text-foreground font-outfit truncate">{value}</p>
              {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
            </div>
          </div>
          {trend !== undefined && (
            <div className={`flex items-center gap-1 text-xs font-medium ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {Math.abs(trendValue || trend).toFixed(1)}%
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Data Table Component
const DataTable = ({ columns, data, onRowClick, loading, emptyMessage = "Nuk ka të dhëna" }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <BarChart3 className="w-10 h-10 mb-2" />
        <p>{emptyMessage}</p>
      </div>
    );
  }
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            {columns.map((col, i) => (
              <th key={i} className={`px-3 py-3 text-left font-medium text-muted-foreground ${col.className || ''}`}>
                {col.header}
              </th>
            ))}
            {onRowClick && <th className="px-3 py-3 w-10"></th>}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr 
              key={rowIndex} 
              className={`border-b border-border hover:bg-muted/30 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {columns.map((col, colIndex) => (
                <td key={colIndex} className={`px-3 py-3 ${col.className || ''}`}>
                  {col.render ? col.render(row[col.accessor], row) : row[col.accessor]}
                </td>
              ))}
              {onRowClick && (
                <td className="px-3 py-3">
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Reports = () => {
  const { user } = useAuth();
  const isModerator = user?.role === 'MODERATOR';
  const isAdmin = ['ADMIN', 'SUPER_ADMIN'].includes(user?.role);
  
  const formatLek = (amount) => formatMoney(amount, 'Lek');
  const formatCredits = (amount) => formatMoney(amount, 'Kredite');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // WebSocket for realtime updates
  const { connected, subscribe } = useWebSocket();
  const lastRefreshRef = useRef(Date.now());
  const [realtimeUpdates, setRealtimeUpdates] = useState(0);
  
  // Filter state
  const [period, setPeriod] = useState('30d');
  const [customDates, setCustomDates] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedCharger, setSelectedCharger] = useState('');
  
  // Data state
  const [overviewData, setOverviewData] = useState(null);
  const [sitesData, setSitesData] = useState([]);
  const [chargersData, setChargersData] = useState([]);
  const [sessionsData, setSessionsData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [sites, setSites] = useState([]);
  
  // Drilldown state
  const [drilldownView, setDrilldownView] = useState(null); // { type: 'site' | 'charger', id: string, data: any }
  const [sessionsPage, setSessionsPage] = useState(1);
  
  // Build filter params
  const getFilterParams = useCallback(() => {
    const params = { period };
    if (period === 'custom') {
      params.start_date = customDates.start_date;
      params.end_date = customDates.end_date;
    }
    if (selectedSite) params.site_id = selectedSite;
    if (selectedCharger) params.charger_id = selectedCharger;
    return params;
  }, [period, customDates, selectedSite, selectedCharger]);
  
  // Load sites for filter dropdown
  useEffect(() => {
    const loadSites = async () => {
      try {
        const response = await getSites();
        setSites(response.data || []);
      } catch (error) {
        console.error('Failed to load sites:', error);
      }
    };
    loadSites();
  }, []);
  
  // Fetch data based on active tab
  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = getFilterParams();
    
    try {
      switch (activeTab) {
        case 'overview':
          const overviewRes = await getReportsOverview(params);
          setOverviewData(overviewRes.data);
          break;
        case 'sites':
          const sitesRes = await getReportsSites(params);
          setSitesData(sitesRes.data || []);
          break;
        case 'chargers':
          const chargersRes = await getReportsChargers(params);
          setChargersData(chargersRes.data || []);
          break;
        case 'sessions':
          const sessionsRes = await getReportsSessions({ ...params, page: sessionsPage, page_size: 50 });
          setSessionsData(sessionsRes.data || { items: [], total: 0 });
          break;
      }
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  }, [activeTab, getFilterParams, sessionsPage]);
  
  useEffect(() => {
    if (!drilldownView) {
      fetchData();
    }
  }, [fetchData, drilldownView]);
  
  // Subscribe to realtime session events for auto-refresh
  useEffect(() => {
    if (!connected) return;
    
    // Debounced refresh - refresh data max every 5 seconds
    const handleSessionEvent = (data) => {
      const now = Date.now();
      if (now - lastRefreshRef.current > 5000) {
        lastRefreshRef.current = now;
        setRealtimeUpdates(prev => prev + 1);
        
        // Only refetch if on overview or sessions tab and not drilling down
        if (!drilldownView && (activeTab === 'overview' || activeTab === 'sessions')) {
          fetchData();
        }
      }
    };
    
    // Subscribe to session events
    const unsubStarted = subscribe(EventTypes.SESSION_STARTED, handleSessionEvent);
    const unsubCompleted = subscribe(EventTypes.SESSION_COMPLETED, handleSessionEvent);
    
    return () => {
      unsubStarted();
      unsubCompleted();
    };
  }, [connected, subscribe, activeTab, drilldownView, fetchData]);
  
  // Drilldown handlers
  const handleSiteDrilldown = async (site) => {
    setLoading(true);
    try {
      const response = await getReportsSiteDetail(site.site_id, getFilterParams());
      setDrilldownView({ type: 'site', id: site.site_id, data: response.data });
    } catch (error) {
      toast.error('Gabim në ngarkimin e detajeve');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChargerDrilldown = async (charger) => {
    setLoading(true);
    try {
      const response = await getReportsChargerDetail(charger.charger_id, getFilterParams());
      setDrilldownView({ type: 'charger', id: charger.charger_id, data: response.data });
    } catch (error) {
      toast.error('Gabim në ngarkimin e detajeve');
    } finally {
      setLoading(false);
    }
  };
  
  const closeDrilldown = () => setDrilldownView(null);
  
  // Export handlers
  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };
  
  const handleExport = async (type, format) => {
    setExporting(true);
    const params = getFilterParams();
    
    try {
      let response;
      let filename;
      const timestamp = new Date().toISOString().split('T')[0];
      
      switch (type) {
        case 'overview':
          response = await exportReportsOverviewPDF(params);
          filename = `AlbMove_Raport_${period}_${timestamp}.pdf`;
          break;
        case 'sessions':
          response = format === 'csv' 
            ? await exportReportsSessionsCSV(params)
            : await exportReportsSessionsPDF(params);
          filename = `sesione_${timestamp}.${format}`;
          break;
        case 'sites':
          response = format === 'csv'
            ? await exportReportsSitesCSV(params)
            : await exportReportsSitesPDF(params);
          filename = `stacione_${timestamp}.${format}`;
          break;
        case 'chargers':
          response = format === 'csv'
            ? await exportReportsChargersCSV(params)
            : await exportReportsChargersPDF(params);
          filename = `karikues_${timestamp}.${format}`;
          break;
        case 'charger-detail':
          response = await exportReportsChargerDetailPDF(drilldownView.id, params);
          filename = `karikues_${drilldownView.id}_${timestamp}.pdf`;
          break;
        case 'site-detail':
          response = await exportReportsSiteDetailPDF(drilldownView.id, params);
          filename = `stacion_${drilldownView.id}_${timestamp}.pdf`;
          break;
        default:
          return;
      }
      
      downloadBlob(response.data, filename);
      toast.success('Raporti u shkarkua me sukses', { description: filename });
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Gabim në eksportimin e raportit');
    } finally {
      setExporting(false);
    }
  };
  
  // Site detail columns
  const siteColumns = [
    { header: 'Stacioni', accessor: 'site_name', render: (v) => <span className="font-medium">{v}</span> },
    { header: 'Adresa', accessor: 'address', className: 'hidden md:table-cell' },
    { header: 'Karikues', accessor: 'charger_count', className: 'text-center' },
    { header: 'Energji (kWh)', accessor: 'total_energy_kwh', render: (v) => formatEnergy(v), className: 'text-right' },
    { header: 'Sesione', accessor: 'total_sessions', className: 'text-right' },
    { header: 'Të Ardhura (Topup)', accessor: 'total_revenue_lek', render: (v) => formatLek(v), className: 'text-right font-medium' },
  ];
  
  // Charger columns
  const chargerColumns = [
    { header: 'Karikuesi', accessor: 'charger_id', render: (v, row) => <span className="font-medium">{v || row.charger_name}</span> },
    { header: 'Stacioni', accessor: 'site_name', className: 'hidden md:table-cell' },
    { header: 'Statusi', accessor: 'status', render: (v, row) => (
      <Badge variant={row.is_online ? 'default' : 'destructive'} className={row.is_online ? 'bg-green-500' : 'bg-red-500'}>
        {row.is_online ? 'Online' : 'Offline'}
      </Badge>
    )},
    { header: 'kWh', accessor: 'total_energy_kwh', render: (v) => formatEnergy(v), className: 'text-right' },
    { header: 'Sesione', accessor: 'total_sessions', className: 'text-right' },
    { header: 'Të Ardhura (Topup)', accessor: 'total_revenue_lek', render: (v) => formatLek(v), className: 'text-right font-medium' },
  ];
  
  // Sessions columns
  const sessionColumns = [
    { header: 'Nisur më', accessor: 'started_at', render: (v) => v ? formatAlbanianDate(v, true) : '-' },
    { header: 'Stacioni', accessor: 'site_name', className: 'hidden lg:table-cell' },
    { header: 'Karikuesi', accessor: 'charger_id', render: (v, row) => v || row.charger_name || '-' },
    { header: 'Porta', accessor: 'connector_id', render: (v) => v ? `Porta ${v}` : '-', className: 'text-center' },
    { header: 'Kohëzgjatja', accessor: 'duration_min', render: (v) => `${v?.toFixed(0) || 0} min`, className: 'hidden md:table-cell' },
    { header: 'kWh', accessor: 'energy_kwh', render: (v) => v?.toFixed(2), className: 'text-right' },
    { header: 'Kredite të Përdorura', accessor: 'total_lek', render: (v) => formatCredits(v), className: 'text-right font-medium' },
    { header: 'Klienti', accessor: 'client_name', className: 'hidden xl:table-cell' },
  ];

  // Render Overview Tab
  const renderOverview = () => {
    if (!overviewData) return null;
    const { kpi, time_series, top_sites, top_chargers, top_clients } = overviewData;
    
    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          <KPICard 
            title="Energjia e Konsumuar" 
            value={formatEnergy(kpi.total_energy_kwh)} 
            icon={Zap} 
            color="blue"
            trend={kpi.energy_trend_pct}
          />
          <KPICard 
            title="Sesione Karikimi" 
            value={kpi.total_sessions.toLocaleString()} 
            icon={Activity} 
            color="primary"
            trend={kpi.sessions_trend_pct}
          />
          <KPICard 
            title="Të Ardhura (Topup)" 
            value={formatLek(kpi.total_revenue_lek)} 
            icon={Wallet} 
            color="green"
            trend={kpi.revenue_trend_pct}
          />
          <KPICard 
            title="Kredite të Përdorura" 
            value={formatCredits(kpi.total_credits_used_lek || 0)} 
            icon={Wallet} 
            color="yellow"
            trend={kpi.credits_used_trend_pct || 0}
          />
          <KPICard 
            title="Mesatarja/Sesion" 
            value={formatEnergy(kpi.avg_kwh_per_session)} 
            icon={BarChart3} 
            color="yellow"
          />
          <KPICard 
            title="Kohëzgjatja Mes." 
            value={`${kpi.avg_session_duration_min?.toFixed(0) || 0} min`} 
            icon={Clock} 
            color="purple"
          />
        </div>
        
        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Time Series Chart */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-outfit">Trendi i Konsumit të Energjisë dhe Të Ardhurave (Topup)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={time_series || []}>
                    <defs>
                      <linearGradient id="colorEnergy" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1d62a7" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#1d62a7" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#afd039" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#afd039" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => { if (!v) return ''; const parts = v.split('-'); return parts.length >= 3 ? `${parts[2]}/${parts[1]}` : v.slice(5); }} />
                    <YAxis yAxisId="left" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `${Number(v).toFixed(0)} kWh`} />
                    <YAxis yAxisId="right" orientation="right" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `${Number(v).toFixed(0)} Lek`} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181b', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#fff'
                      }}
                      labelStyle={{ color: '#a1a1aa' }}
                      labelFormatter={(label) => { 
                        if (!label) return ''; 
                        const parts = label.split('-'); 
                        return parts.length >= 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : label; 
                      }}
                      formatter={(value, name) => [
                        name === 'energy_kwh' 
                          ? `${Number(value).toFixed(2)} kWh` 
                          : `${Number(value).toFixed(2)} Lek`,
                        name === 'energy_kwh' ? 'Energji' : 'Të Ardhura (Topup)'
                      ]}
                      cursor={{ stroke: 'hsl(var(--primary))', strokeWidth: 1, strokeDasharray: '5 5' }}
                    />
                    <Area 
                      yAxisId="left" 
                      type="monotone" 
                      dataKey="energy_kwh" 
                      stroke="#1d62a7" 
                      fill="url(#colorEnergy)" 
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6, stroke: '#1d62a7', strokeWidth: 2, fill: 'hsl(var(--card))', className: 'animate-pulse' }}
                    />
                    <Area 
                      yAxisId="right" 
                      type="monotone" 
                      dataKey="revenue_lek" 
                      stroke="#afd039" 
                      fill="url(#colorRevenue)" 
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6, stroke: '#afd039', strokeWidth: 2, fill: 'hsl(var(--card))', className: 'animate-pulse' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          {/* Sessions Chart */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-outfit">Sesione sipas Ditës</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={time_series || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => { if (!v) return ''; const parts = v.split('-'); return parts.length >= 3 ? `${parts[2]}/${parts[1]}` : v.slice(5); }} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181b', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#fff'
                      }}
                      labelStyle={{ color: '#a1a1aa' }}
                      labelFormatter={(label) => { 
                        if (!label) return ''; 
                        const parts = label.split('-'); 
                        return parts.length >= 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : label; 
                      }}
                      formatter={(value) => [`${value} sesione`, 'Sesione']}
                    />
                    <Bar dataKey="sessions" fill="#1d62a7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Top Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Sites */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-outfit flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Top Stacionet
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {(top_sites || []).map((site, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 cursor-pointer" onClick={() => handleSiteDrilldown(site)}>
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">{i + 1}</span>
                      <span className="font-medium text-sm">{site.site_name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{formatEnergy(site.energy_kwh)}</p>
                      <p className="text-xs text-muted-foreground">{formatLek(site.revenue_lek)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Top Chargers */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-outfit flex items-center gap-2">
                <BatteryCharging className="w-4 h-4" />
                Top Karikuesit
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {(top_chargers || []).map((charger, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 cursor-pointer" onClick={() => handleChargerDrilldown(charger)}>
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-green-500/10 text-green-500 flex items-center justify-center text-xs font-medium">{i + 1}</span>
                      <div>
                        <span className="font-medium text-sm">{charger.charger_id || charger.charger_name}</span>
                        {charger.charger_name && charger.charger_id !== charger.charger_name && (
                          <p className="text-xs text-muted-foreground">{charger.charger_name}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{formatEnergy(charger.energy_kwh)}</p>
                      <p className="text-xs text-muted-foreground">{charger.sessions} sesione</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Top Clients */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-outfit flex items-center gap-2">
                <Users className="w-4 h-4" />
                Top Klientët
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {(top_clients || []).map((client, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center text-xs font-medium">{i + 1}</span>
                      <div>
                        <span className="font-medium text-sm">{client.user_name}</span>
                        <p className="text-xs text-muted-foreground">{client.user_email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{formatLek(client.total_spent_lek)}</p>
                      <p className="text-xs text-muted-foreground">{client.sessions} sesione • {formatEnergy(client.energy_kwh)}</p>
                    </div>
                  </div>
                ))}
                {(!top_clients || top_clients.length === 0) && (
                  <div className="px-4 py-6 text-center text-muted-foreground text-sm">
                    Nuk ka të dhëna për klientët
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };
  
  // Render Site Drilldown
  const renderSiteDrilldown = () => {
    const { data } = drilldownView;
    if (!data) return null;
    
    const { site, kpi, chargers } = data;
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={closeDrilldown}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Kthehu
          </Button>
          <div className="flex-1">
            <h2 className="text-xl font-bold font-outfit">{site.name}</h2>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {site.address || site.zone || 'Pa adresë'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => handleExport('site-detail', 'pdf')} disabled={exporting}>
            <Download className="w-4 h-4 mr-2" /> Eksporto PDF
          </Button>
        </div>
        
        {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          <KPICard title="Karikues" value={kpi.charger_count} icon={BatteryCharging} color="primary" />
          <KPICard title="Energji (kWh)" value={formatEnergy(kpi.total_energy_kwh)} icon={Zap} color="blue" />
          <KPICard title="Sesione" value={kpi.total_sessions} icon={Activity} color="yellow" />
          <KPICard title="Të Ardhura (Topup)" value={formatLek(kpi.total_revenue_lek)} icon={Wallet} color="green" />
          <KPICard title="Kredite të Përdorura" value={formatCredits(kpi.total_credits_used_lek || 0)} icon={Wallet} color="yellow" />
        </div>
        
        {/* Chargers in this site */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base font-outfit">Karikuesit në këtë Stacion</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable 
              columns={[
                { header: 'Karikuesi', accessor: 'charger_id', render: (v, row) => <span className="font-medium">{v || row.charger_name}</span> },
                { header: 'Statusi', accessor: 'status', render: (v, row) => (
                  <Badge variant={row.is_online ? 'default' : 'destructive'} className={row.is_online ? 'bg-green-500' : 'bg-red-500'}>
                    {row.is_online ? 'Online' : 'Offline'}
                  </Badge>
                )},
                { header: 'kWh', accessor: 'energy_kwh', render: (v) => formatEnergy(v), className: 'text-right' },
                { header: 'Sesione', accessor: 'sessions', className: 'text-right' },
                { header: 'Të Ardhura (Topup)', accessor: 'revenue_lek', render: (v) => formatLek(v), className: 'text-right font-medium' },
              ]}
              data={chargers || []}
              onRowClick={handleChargerDrilldown}
            />
          </CardContent>
        </Card>
      </div>
    );
  };
  
  // Render Charger Drilldown
  const renderChargerDrilldown = () => {
    const { data } = drilldownView;
    if (!data) return null;
    
    const { charger, site, kpi, connectors, time_series } = data;
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={closeDrilldown}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Kthehu
          </Button>
          <div className="flex-1">
            <h2 className="text-xl font-bold font-outfit">{charger.charger_id}</h2>
            <p className="text-sm text-muted-foreground">
              {charger.vendor} {charger.model} • {site?.name || 'Pa stacion'}
            </p>
          </div>
          <Badge variant={charger.is_online ? 'default' : 'destructive'} className={charger.is_online ? 'bg-green-500' : 'bg-red-500'}>
            {charger.is_online ? 'Online' : 'Offline'}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => handleExport('charger-detail', 'pdf')} disabled={exporting}>
            <Download className="w-4 h-4 mr-2" /> Eksporto PDF
          </Button>
        </div>
        
        {/* Charger Info */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><span className="text-muted-foreground">Fuqia Max:</span> <span className="font-medium">{charger.max_power_kw} kW</span></div>
              <div><span className="text-muted-foreground">Çmimi/kWh:</span> <span className="font-medium">{charger.price_per_kwh} Kredite</span></div>
              <div><span className="text-muted-foreground">Statusi:</span> <span className="font-medium">{charger.status}</span></div>
              <div><span className="text-muted-foreground">Zona:</span> <span className="font-medium">{site?.zone || '-'}</span></div>
            </div>
          </CardContent>
        </Card>
        
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          <KPICard title="Energji (kWh)" value={formatEnergy(kpi.total_energy_kwh)} icon={Zap} color="blue" />
          <KPICard title="Sesione" value={kpi.total_sessions} icon={Activity} color="primary" />
          <KPICard title="Të Ardhura (Topup)" value={formatLek(kpi.total_revenue_lek)} icon={Wallet} color="green" />
          <KPICard title="Kredite të Përdorura" value={formatCredits(kpi.total_credits_used_lek || 0)} icon={Wallet} color="yellow" />
          <KPICard title="Mes. kWh/sesion" value={formatEnergy(kpi.avg_kwh_per_session)} icon={BarChart3} color="yellow" />
        </div>
        
        {/* Connectors Breakdown */}
        {connectors && connectors.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base font-outfit">Performanca sipas Portës</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {connectors.map((conn, i) => (
                  <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                        {conn.connector_label}
                      </div>
                      <div>
                        <p className="font-medium">{conn.connector_type}</p>
                        <p className="text-xs text-muted-foreground">{conn.sessions} sesione</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatEnergy(conn.energy_kwh)}</p>
                      <p className="text-xs text-muted-foreground">Të ardhura: {formatLek(conn.revenue_lek)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Time Series Chart */}
        {time_series && time_series.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base font-outfit">Trendi në Kohë</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={time_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => { if (!v) return ''; const parts = v.split('-'); return parts.length >= 3 ? `${parts[2]}/${parts[1]}` : v.slice(5); }} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                    <Line type="monotone" dataKey="energy_kwh" stroke="#1d62a7" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground font-outfit">Raporte & Analitika</h1>
            {connected && (
              <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30 gap-1 animate-pulse" data-testid="live-indicator">
                <Radio className="w-3 h-3" />
                Live
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground mt-1">
            Analiza enterprise e performancës së rrjetit
            {realtimeUpdates > 0 && <span className="text-xs ml-2 text-primary">• Përditësuar automatikisht</span>}
          </p>
        </div>
        
        {/* Global Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={period} onValueChange={(v) => { setPeriod(v); setDrilldownView(null); }}>
            <SelectTrigger className="w-[130px]" data-testid="period-filter">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {period === 'custom' && (
            <>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none z-10" />
                <Input 
                  type="date" 
                  value={customDates.start_date}
                  onChange={(e) => setCustomDates(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-[160px] pl-10 bg-zinc-800 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                />
              </div>
              <span className="text-zinc-500">-</span>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none z-10" />
                <Input 
                  type="date" 
                  value={customDates.end_date}
                  onChange={(e) => setCustomDates(prev => ({ ...prev, end_date: e.target.value }))}
                  className="w-[160px] pl-10 bg-zinc-800 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                />
              </div>
            </>
          )}
          
          <Select value={selectedSite} onValueChange={(v) => { setSelectedSite(v === 'all' ? '' : v); setDrilldownView(null); }}>
            <SelectTrigger className="w-[160px]" data-testid="site-filter">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Të gjitha stacionet" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Të gjitha stacionet</SelectItem>
              {sites.map(site => (
                <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      
      {/* Drilldown Views */}
      {drilldownView?.type === 'site' && renderSiteDrilldown()}
      {drilldownView?.type === 'charger' && renderChargerDrilldown()}
      
      {/* Main Tabs */}
      {!drilldownView && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <TabsList className="grid grid-cols-4 w-full sm:w-auto">
              <TabsTrigger value="overview" data-testid="tab-overview">Përmbledhje</TabsTrigger>
              <TabsTrigger value="sites" data-testid="tab-sites">Stacione</TabsTrigger>
              <TabsTrigger value="chargers" data-testid="tab-chargers">Karikues</TabsTrigger>
              <TabsTrigger value="sessions" data-testid="tab-sessions">Sesione Karikimi</TabsTrigger>
            </TabsList>
            
            {/* Export Buttons */}
            <div className="flex gap-2">
              {activeTab === 'overview' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={exporting}
                  onClick={() => handleExport('overview', 'pdf')}
                  data-testid="export-overview-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Eksporto PDF
                </Button>
              )}
              {activeTab === 'sessions' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={exporting} data-testid="export-sessions-btn">
                      <Download className="w-4 h-4 mr-2" /> Eksporto
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleExport('sessions', 'csv')}>
                      <FileText className="w-4 h-4 mr-2 text-blue-500" /> CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('sessions', 'pdf')}>
                      <FileSpreadsheet className="w-4 h-4 mr-2 text-red-500" /> PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {activeTab === 'sites' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={exporting} data-testid="export-sites-btn">
                      <Download className="w-4 h-4 mr-2" /> Eksporto
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleExport('sites', 'csv')}>
                      <FileText className="w-4 h-4 mr-2 text-blue-500" /> CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('sites', 'pdf')}>
                      <FileSpreadsheet className="w-4 h-4 mr-2 text-red-500" /> PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {activeTab === 'chargers' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={exporting} data-testid="export-chargers-btn">
                      <Download className="w-4 h-4 mr-2" /> Eksporto
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleExport('chargers', 'csv')}>
                      <FileText className="w-4 h-4 mr-2 text-blue-500" /> CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('chargers', 'pdf')}>
                      <FileSpreadsheet className="w-4 h-4 mr-2 text-red-500" /> PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          
          <TabsContent value="overview" className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : renderOverview()}
          </TabsContent>
          
          <TabsContent value="sites" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-base font-outfit">Raport sipas Stacionit</CardTitle>
                <CardDescription>Kliko mbi një rresht për të parë detajet</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <DataTable 
                  columns={siteColumns} 
                  data={sitesData} 
                  loading={loading}
                  onRowClick={handleSiteDrilldown}
                  emptyMessage="Nuk ka të dhëna për stacionet"
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="chargers" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-base font-outfit">Raport sipas Karikuesit</CardTitle>
                <CardDescription>Kliko mbi një rresht për të parë detajet</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <DataTable 
                  columns={chargerColumns} 
                  data={chargersData} 
                  loading={loading}
                  onRowClick={handleChargerDrilldown}
                  emptyMessage="Nuk ka të dhëna për karikuesit"
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="sessions" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-outfit">Sesione Karikimi</CardTitle>
                    <CardDescription>
                      {sessionsData.total > 0 ? `${sessionsData.total.toLocaleString()} sesione gjithsej` : 'Nuk ka sesione'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <DataTable 
                  columns={sessionColumns} 
                  data={sessionsData.items || []} 
                  loading={loading}
                  emptyMessage="Nuk ka sesione për këtë periudhë"
                />
                
                {/* Pagination */}
                {sessionsData.total_pages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                      Faqja {sessionsData.page} nga {sessionsData.total_pages}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={sessionsPage <= 1}
                        onClick={() => setSessionsPage(p => p - 1)}
                      >
                        Para
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={sessionsPage >= sessionsData.total_pages}
                        onClick={() => setSessionsPage(p => p + 1)}
                      >
                        Pas
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default Reports;
